import React from 'react';
import { Button, Space, Card } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { Service } from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

type NewsRow = Record<string, unknown> & {
  id: number;
  title?: string;
  summary?: string;
  body?: string;
  publishedAt?: string;
  coverImage?: string;
};

const normalizeRows = (rows: unknown[]): NewsRow[] => {
  return (rows || []).map((r: unknown) => {
    const item = r as Record<string, unknown>;
    const data = (item.data as Record<string, unknown>) || item;
    return { ...item, ...data, id: (data.id as number) ?? (item.id as number) } as NewsRow;
  });
};

const NewsManagement: React.FC = () => {
  const navigate = useNavigate();

  const fetchApi = async (pn: number, rn: number, custom?: string) => {
    const res = await Service.ycNewsControllerGet(pn, rn, custom);
    const raw = (res as { data?: { rows?: unknown[]; count?: number; pn?: number } })?.data;
    const rows = raw?.rows ?? [];
    return {
      errNo: (res as { errNo?: number }).errNo ?? 0,
      data: {
        rows: normalizeRows(rows),
        count: raw?.count ?? 0,
        pn: raw?.pn ?? pn,
      },
    };
  };

  const {
    data: list,
    loading,
    pagination,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<NewsRow>({
    fetchData: fetchApi,
    deleteData: async (id) => { await Service.ycNewsControllerDelete(id); },
    errorMessage: '获取新闻列表失败',
    searchOptions: { fields: ['title', 'summary'] },
  });

  const columns: ColumnsType<NewsRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    createLongTextColumn('标题', 'title', 20),
    createLongTextColumn('摘要', 'summary', 20),
    createLongTextColumn('发布时间', 'publishedAt', 12),
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  const stats: DataStat[] = [
    { title: '新闻总数', value: pagination.total, prefix: <FileTextOutlined />, valueStyle: { color: '#3f8600' } },
    { title: '当前页', value: list.length, valueStyle: { color: '#1890ff' } },
  ];

  const actionButtons: ActionButton<NewsRow>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: (record) => navigate(`/cms/news/edit/${record.id}`),
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: (record) => handleDelete(record.id),
      confirm: { title: '确定删除该新闻？', okText: '确定', cancelText: '取消' },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        <DataStats stats={stats} className={styles.statsRow} />
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/cms/news/create')}>
              新增新闻
            </Button>
            <SearchBar search={search} placeholder="搜索标题、摘要" width={300} loading={loading} />
          </Space>
        </div>
        <DataTable
          columns={columns}
          dataSource={list}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>
    </div>
  );
};

export default NewsManagement;
